import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const mockData = [
  { id: 1, mainWord: "Acharang", misspellWords: "Acaarnga,acaranga" },
  { id: 2, mainWord: "Acharaya", misspellWords: "Achaya,achaya,aachaya" },
  {
    id: 3,
    mainWord: "Agam",
    misspellWords: "Aagam,aagaam,agama,aagama,aagamsar",
  },
  { id: 4, mainWord: "Alambil", misspellWords: "Aalambill,ayambil,ayambeel" },
  { id: 5, mainWord: "Ajitnath", misspellWords: "Ajitnathji" },
  { id: 6, mainWord: "Ananthath", misspellWords: "Anantdev,anaantnath" },
  { id: 7, mainWord: "Anekantamar", misspellWords: "Anekantvaad" },
  { id: 8, mainWord: "Ang", misspellWords: "Anga" },
];

const MisspelledWord = () => {
  const [selectedLetter, setSelectedLetter] = useState("All");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setEditDialogOpen(true);
  };

  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="mb-1 text-3xl font-bold text-foreground">
              User Misspelled Word
            </h2>
            <p className="text-sm text-muted-foreground">Record Count: 89</p>
          </div>
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
            Export Data
          </Button>
        </div>

        {/* Alphabet Filter */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedLetter === "All" ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedLetter("All")}
            className="h-8"
          >
            All
          </Button>
          {alphabet.map(letter => (
            <Button
              key={letter}
              variant={selectedLetter === letter ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedLetter(letter)}
              className="h-8 w-8 p-0"
            >
              {letter}
            </Button>
          ))}
        </div>

        {/* Search */}
        <div>
          <div className="mb-2 text-sm font-medium">Search</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              All
            </Button>
            <Button variant="outline" size="sm">
              City
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-foreground">
                  SR.NO.
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-foreground">
                  MAIN WORD
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-foreground">
                  MISSPELL WORDS
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-foreground">
                  ACTION
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {mockData.map((item, index) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className="px-6 py-3 text-sm">{index + 1}</td>
                  <td className="px-6 py-3 text-sm">{item.mainWord}</td>
                  <td className="px-6 py-3 text-sm">{item.misspellWords}</td>
                  <td className="px-6 py-3">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="h-8 w-8 rounded-full bg-primary p-0 text-primary-foreground hover:bg-primary/90"
                        onClick={() => handleEdit(item)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        className="h-8 w-8 rounded-full bg-destructive p-0 text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => handleDelete(item)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm">
            « First
          </Button>
          <Button variant="outline" size="sm">
            ‹ Back
          </Button>
          <Button variant="outline" size="sm">
            1
          </Button>
          <Button size="sm" className="bg-primary text-primary-foreground">
            2
          </Button>
          <Button variant="outline" size="sm">
            3
          </Button>
          <Button variant="outline" size="sm">
            4
          </Button>
          <Button variant="outline" size="sm">
            ...
          </Button>
          <Button variant="outline" size="sm">
            120
          </Button>
          <Button variant="outline" size="sm">
            Next ›
          </Button>
          <Button variant="outline" size="sm">
            Last »
          </Button>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Misspelled Word</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label required>Main Word</Label>
              <Input defaultValue={selectedItem?.mainWord} />
            </div>
            <div>
              <Label required>Misspelled Words</Label>
              <Input defaultValue={selectedItem?.misspellWords} />
            </div>
          </div>
          <DialogFooter>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
              Save
            </Button>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Delete Row</DialogTitle>
          </DialogHeader>
          <p className="text-center">
            Are you sure you want to delete this row?
          </p>
          <DialogFooter className="justify-center">
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default MisspelledWord;
